import { Archive, Plus, Target } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GoalManagerCard } from "@/components/GoalManagerCard";
import {
  GoalDeleteModal,
  GoalTitleModal,
} from "@/components/GoalManagementModals";
import { TopBar } from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";

const emptyCopy = {
  active: "Você ainda não tem metas ativas.",
  archived: "Nenhuma meta foi arquivada ainda.",
};

export default function Goals() {
  const navigate = useNavigate();
  const [scope, setScope] = useState("active");
  const [goals, setGoals] = useState(null);
  const [error, setError] = useState("");
  const [editingGoal, setEditingGoal] = useState(null);
  const [deletingGoal, setDeletingGoal] = useState(null);

  const loadGoals = useCallback(async () => {
    setError("");
    setGoals(null);
    try {
      const { data } = await api.get(`/goals?scope=${scope}`);
      setGoals(data);
    } catch (requestError) {
      setError(formatApiError(requestError));
      setGoals([]);
    }
  }, [scope]);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const updateGoalInList = (updatedGoal) => {
    setGoals((items) => items.map((item) => item.id === updatedGoal.id ? updatedGoal : item));
  };

  const archive = async (goal) => {
    const endpoint = goal.is_archived ? "restore" : "archive";
    try {
      await api.post(`/goals/${goal.id}/${endpoint}`);
      toast.success(goal.is_archived ? "Meta reativada." : "Meta arquivada.");
      loadGoals();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    }
  };

  const remove = (goalId) => {
    setGoals((items) => items.filter((goal) => goal.id !== goalId));
  };

  return (
    <div data-testid="page-minhas-metas" className="min-h-screen cofre-bg">
      <TopBar />
      <main className="mx-auto max-w-6xl px-5 py-10 sm:py-14">
        <section className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Seus desafios</p>
            <h1 className="mt-2 font-heading text-4xl font-extrabold text-emerald-950 sm:text-5xl">Minhas metas</h1>
            <p className="mt-3 max-w-xl text-lg text-slate-700">Cada cofrinho guarda seu próprio ritmo, valores e histórico.</p>
          </div>
          <button type="button" onClick={() => navigate("/metas/nova")} data-testid="btn-create-new-goal" className="inline-flex min-h-[54px] items-center gap-2 rounded-xl bg-emerald-700 px-5 text-lg font-bold text-white shadow-[0_5px_0_#064e3b] hover:bg-emerald-800"><Plus className="h-5 w-5" />Nova meta</button>
        </section>
        <div className="mt-10 flex gap-2 border-b border-amber-200" role="tablist" aria-label="Lista de metas">
          <button type="button" onClick={() => setScope("active")} data-testid="tab-active-goals" aria-selected={scope === "active"} className={`min-h-[48px] border-b-4 px-4 text-lg font-bold transition-colors ${scope === "active" ? "border-emerald-700 text-emerald-950" : "border-transparent text-slate-600 hover:text-emerald-800"}`}>Ativas</button>
          <button type="button" onClick={() => setScope("archived")} data-testid="tab-archived-goals" aria-selected={scope === "archived"} className={`min-h-[48px] border-b-4 px-4 text-lg font-bold transition-colors ${scope === "archived" ? "border-emerald-700 text-emerald-950" : "border-transparent text-slate-600 hover:text-emerald-800"}`}>Arquivadas</button>
        </div>
        <section className="mt-7">
          {error && <div data-testid="goals-manager-error" role="alert" className="rounded-2xl border-2 border-red-200 bg-red-50 p-5 text-lg text-red-800"><p>{error}</p><button type="button" onClick={loadGoals} data-testid="goals-manager-retry-button" className="mt-3 min-h-[44px] rounded-xl border-2 border-red-300 bg-white px-4 font-bold text-red-800 hover:bg-red-100">Tentar novamente</button></div>}
          {!error && goals === null && <div data-testid="goals-manager-loading" className="h-52 animate-pulse rounded-3xl bg-white/60" />}
          {!error && goals?.length === 0 && <div data-testid="goals-manager-empty" className="rounded-3xl border-2 border-dashed border-amber-300 bg-white/70 p-10 text-center"><Archive className="mx-auto h-10 w-10 text-amber-700" /><p className="mt-4 text-xl font-bold text-emerald-950">{emptyCopy[scope]}</p>{scope === "active" && <button type="button" onClick={() => navigate("/metas/nova")} data-testid="goals-empty-create-button" className="mt-6 min-h-[50px] rounded-xl bg-emerald-700 px-5 text-lg font-bold text-white hover:bg-emerald-800">Criar minha meta</button>}</div>}
          {!error && goals?.length > 0 && <ul data-testid="managed-goals-list" className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">{goals.map((goal) => <GoalManagerCard key={goal.id} goal={goal} archived={scope === "archived"} onEdit={setEditingGoal} onArchive={archive} onDelete={setDeletingGoal} />)}</ul>}
        </section>
      </main>
      {editingGoal && <GoalTitleModal goal={editingGoal} onClose={() => setEditingGoal(null)} onSaved={updateGoalInList} />}
      {deletingGoal && <GoalDeleteModal goal={deletingGoal} onClose={() => setDeletingGoal(null)} onDeleted={remove} />}
    </div>
  );
}